import { useState, useEffect, useRef } from "react";

interface PdfViewerProps {
  base64: string;
  documentTitle: string;
}

export const PdfViewer = ({ base64, documentTitle }: PdfViewerProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Clean base64 string
  const cleanBase64 = base64.startsWith("data:")
    ? base64.split(",")[1] || base64
    : base64;

  useEffect(() => {
    const loadPdf = async () => {
      try {
        // Check if PDF.js is already loaded
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let pdfjsLib = (window as any).pdfjsLib;
        
        if (!pdfjsLib) {
          // Load PDF.js from CDN
          const script = document.createElement("script");
          script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
          script.async = true;
          
          await new Promise<void>((resolve, reject) => {
            script.onload = () => {
              // @ts-ignore
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              pdfjsLib = (window as any).pdfjsLib;
              if (!pdfjsLib) {
                reject(new Error("PDF.js library failed to load"));
              } else {
                resolve();
              }
            };
            script.onerror = () => {
              reject(new Error("Failed to load PDF.js script"));
            };
            document.head.appendChild(script);
          });
        }

        if (!pdfjsLib) {
          throw new Error("PDF.js library not available");
        }

        pdfjsLib.GlobalWorkerOptions.workerSrc = 
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

        // Convert base64 to Uint8Array
        const binaryString = atob(cleanBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Load PDF document
        const loadingTask = pdfjsLib.getDocument({ data: bytes });
        const pdf = await loadingTask.promise;

        // Render all pages
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
          
          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale: 1.5 });
            
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            if (!context) {
              throw new Error("Failed to get canvas context");
            }
            
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            
            await page.render({
              canvasContext: context,
              viewport: viewport
            }).promise;
            
            const pageContainer = document.createElement("div");
            pageContainer.style.marginBottom = "20px";
            pageContainer.style.textAlign = "center";
            pageContainer.appendChild(canvas);
            
            if (containerRef.current) {
              containerRef.current.appendChild(pageContainer);
            }
          }
          
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error loading PDF:", error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    if (cleanBase64 && containerRef.current) {
      loadPdf();
    }

    return () => {
      // Don't remove script as it might be used by other components
    };
  }, [cleanBase64]);

  if (hasError) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
          color: "#DC2626",
          fontSize: "14px",
          textAlign: "center"
        }}
      >
        Erro ao carregar o PDF. Tente novamente.
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        backgroundColor: "#FFFFFF"
      }}
    >
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#FFFFFF",
            zIndex: 1
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #E5E7EB",
              borderTop: "4px solid #1D4ED8",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }}
          />
          <div style={{ marginTop: "16px", color: "#6B7280", fontSize: "14px" }}>
            Carregando PDF...
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          overflow: "auto",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}
      />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
